import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`A cohesive experience`}</h2>
    <p>{`GitHub has a global audience. Millions of people use GitHub every day for many different reasons, in many different ways. They all come from different backgrounds. We can’t infer their physical or cognitive abilities, or devices they use, or the internet connection they have. All of that needs to be taken into account before, during, and after the design process.`}</p>
    <p>{`Familiar patterns help people intuitively navigate to the information they're looking for, while also helping people discover new features. If pages and features don’t share the same metaphors, people get confused more easily, it makes it harder for them to understand the features we provide.`}</p>
    <p>{`Primer focuses on the user experience so people can benefit from using a platform that is cohesive, no matter where in the platform they are. If a page behaves and feels familiar, it allows people to focus on the task at hand, instead of requiring them to parse UI that looks completely new.`}</p>
    <h2>{`Inclusive and accessible design`}</h2>
    <p>{`GitHub is a home for all developers. To be inclusive means we must consider accessibility at the core of how we design.`}</p>
    <p>{`Accessibility and inclusive design needs to be considered from the start of a project, because it deeply affects its design, how the product behaves, and how it’s built.`}</p>
    <p>{`Adding accessibility support to a design after the fact generally turns into a difficult retrofit.`}</p>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/guides/accessibility/accessibility-at-github"
      }}>{`Accessibility at GitHub`}</a>{`.`}</p>
    <h2>{`Responsive design`}</h2>
    <p>{`Browsers are found on devices with different shapes, sizes, display qualities, and input methods, so we can’t assume a desktop-only experience is enough. Supporting responsive experiences is an essential part of developing for the Web today. In fact, making sure pages work well `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/reflow.html"
      }}>{`on most screen sizes or zoom levels`}</a>{` is an accessibility requirement as well.`}</p>
    <p>{`Primer plays an important role in providing reusable patterns and components that can streamline the creation of responsive-friendly and accessible designs, letting teams focus on bigger problems. GitHub projects should take advantage of these patterns so they can provide people with cohesive experiences throughout the entire platform.`}</p>
    <p>{`Primer uses the term “Responsive design” to refer to design decisions that are adapted to device form factors and user preferences.`}</p>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/foundations/responsive"
      }}>{`Responsive`}</a>{`.`}</p>
    <h2>{`Design for efficiency`}</h2>
    <p>{`GitHub is a platform used for productivity. We aim to encourage flow, focus, and an experience that is fast and compact.`}</p>
    <p>{`By providing a cohesive, inclusive, and responsive design, Primer’s goal is to remove as much friction as possible between the human and the software.`}</p>
    <p>{`Primer patterns and components aim towards accessible solutions that don’t dilute efficient UI interactions. It focuses on responsive design that doesn’t reduce desktop experiences into mobile-first solutions. Ultimately, it recognizes the need to encourage flow by providing calm and logical experiences throughout.`}</p>
    <h2>{`The Zen of GitHub`}</h2>
    <blockquote>
      <p parentName="blockquote">{`“Whether you call it taste, culture, or zen, there are underlying assumptions that members of an organization rely on to resolve ambiguity in pursuit of the organization’s mission.”`}<br parentName="p"></br>{`
`}{`– `}<a parentName="p" {...{
          "href": "https://ben.balter.com/2015/08/12/the-zen-of-github/"
        }}>{`Ben Balter`}</a></p>
    </blockquote>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://api.github.com/zen"
      }}>{`GitHub API`}</a>{` consolidates the Zen of GitHub in its own codebase, in 14 aphorisms:`}</p>
    <ul>
      <li parentName="ul">{`Responsive is better than fast`}</li>
      <li parentName="ul">{`It’s not fully shipped until it’s fast`}</li>
      <li parentName="ul">{`Anything added dilutes everything else`}</li>
      <li parentName="ul">{`Practicality beats purity`}</li>
      <li parentName="ul">{`Approachable is better than simple`}</li>
      <li parentName="ul">{`Mind your words, they are important`}</li>
      <li parentName="ul">{`Speak like a human`}</li>
      <li parentName="ul">{`Half measures are as bad as nothing at all`}</li>
      <li parentName="ul">{`Encourage flow`}</li>
      <li parentName="ul">{`Non-blocking is better than blocking`}</li>
      <li parentName="ul">{`Favor focus over features`}</li>
      <li parentName="ul">{`Avoid administrative distraction`}</li>
      <li parentName="ul">{`Design for failure`}</li>
      <li parentName="ul">{`Keep it logically awesome`}</li>
    </ul>
    <p>{`We recommend you to read about the story behind the Zen of GitHub in these posts:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://warpspire.com/posts/taste"
        }}>{`Taste and The Zen of GitHub`}</a>{`, by `}<a parentName="li" {...{
          "href": "https://github.com/kneath"
        }}>{`@kneath`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://ben.balter.com/2015/08/12/the-zen-of-github/"
        }}>{`The Zen of GitHub`}</a>{`, by `}<a parentName="li" {...{
          "href": "https://github.com/benbalter"
        }}>{`@benbalter`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      